import { useEffect, useRef, useState } from 'react';
import { useGlobalTransactionSyncStatus } from './useGlobalTransactionSyncStatus';

export const useTransactionImportStatusProps = () => {
    const transactionSyncStatus = useGlobalTransactionSyncStatus();
    const prevStatusRef = useRef(transactionSyncStatus);
    const [visible, setVisible] = useState(false);
    const hideTimeoutRef = useRef<NodeJS.Timeout | null>(null);

    useEffect(() => {
        // Clear any existing timeout when status changes
        if (hideTimeoutRef.current) {
            clearTimeout(hideTimeoutRef.current);
            hideTimeoutRef.current = null;
        }

        const prevStatus = prevStatusRef.current;
        const currentStatus = transactionSyncStatus;

        // Ignore undefined status (loading state, or no FEs at all)
        if (!currentStatus) {
            return;
        }

        // First time we're getting a non-undefined status
        if (!prevStatus) {
            // Show for any status except 'synced'
            if (currentStatus !== 'synced') {
                setVisible(true);
            }
        } else {
            // Status transitioned
            if (prevStatus !== currentStatus) {
                setVisible(true);

                if (currentStatus === 'synced') {
                    // Hide after 5 seconds
                    hideTimeoutRef.current = setTimeout(
                        () => setVisible(false),
                        5000
                    );
                } else if (currentStatus === 'failed') {
                    // Hide after 10 seconds
                    hideTimeoutRef.current = setTimeout(
                        () => setVisible(false),
                        10000
                    );
                }
            } else if (
                ['fetching', 'categorizing', 'waiting-for-webhook'].includes(
                    currentStatus
                )
            ) {
                // Always show in-progress statuses, even if status hasn't changed
                // This ensures visibility after page navigation
                setVisible(true);
            }
        }

        prevStatusRef.current = currentStatus;

        return () => {
            if (hideTimeoutRef.current) {
                clearTimeout(hideTimeoutRef.current);
            }
        };
    }, [transactionSyncStatus]);

    return {
        visible,
        transactionSyncStatus,
    };
};
